import type { ModelOutputRecord } from '@/features/forecasts/forecasts.api.types';

/** Internal helpers */
function dateDiffInDays(dateA: string, dateB: string): number {
  const a = new Date(`${dateA}T00:00:00Z`).getTime();
  const b = new Date(`${dateB}T00:00:00Z`).getTime();
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.abs(Math.round((a - b) / msPerDay));
}

function parseIsoDateUtc(dateStr: string): Date | null {
  const [y, m, d] = dateStr.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(Date.UTC(y, m - 1, d));
}

function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Date operations */
export function addDaysToIsoDate(dateStr: string, days: number): string {
  const date = parseIsoDateUtc(dateStr);
  if (!date) return dateStr;

  date.setUTCDate(date.getUTCDate() + days);
  return toIsoDate(date);
}

export function addDaysToMdyDate(dateStr: string, days: number): Date {
  const [m, d, y] = dateStr.split('/').map((value) => Number(value));
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(date.getUTCDate() + days);
  return date;
}

export const isoYearsAhead = (years: number): string => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + years);
  return toIsoDate(d);
};

export const isoYearsAgo = (years: number): string => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - years);
  return toIsoDate(d);
};

export function monthStartsBetweenUtc(minDate: Date, maxDate: Date): Date[] {
  const starts: Date[] = [];
  const cursor = new Date(Date.UTC(minDate.getUTCFullYear(), minDate.getUTCMonth(), 1));

  while (cursor <= maxDate) {
    starts.push(new Date(cursor));
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }

  return starts;
}

export const todayISO = (): string => toIsoDate(new Date());

export function uniqeDatesSorted(arr: string[]) {
  return Array.from(new Set(arr)).sort(); // assumes ISO YYYY-MM-DD
}

/** Date formatting and labels */
export function formatDateMDY(input: string | number | Date, locale?: string): string {
  const date = new Date(input);
  if (isNaN(date.getTime())) return '';

  const userLocale = locale ?? navigator?.language ?? 'en-US';

  return date.toLocaleDateString(userLocale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

export function formatDateTimeLocal(input: string | number | Date, locale?: string): string {
  const date = new Date(input);
  if (isNaN(date.getTime())) return '';

  const userLocale = locale ?? navigator?.language ?? 'en-US';
  return date.toLocaleString(userLocale);
}

export function formatIsoDateLabel(dateStr: string, locale?: string): string {
  const [y, m, d] = dateStr.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';

  // Parse as local calendar date to prevent UTC timezone rollover in labels.
  return formatDateMDY(new Date(y, m - 1, d), locale);
}

export function formatIsoDateMd(dateStr: string): string {
  const [, m, d] = dateStr.slice(0, 10).split('-').map(Number);
  if (!m || !d) return '';
  return `${m}/${d}`;
}

export function formatUtcDateMdy(date: Date): string {
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${month}/${day}/${year}`;
}

/**
 * Generates week labels starting from FCD + 1 week
 * Format: "9-Feb", "16-Feb", "23-Feb", "2-Mar" (or French: "9-févr.", "16-févr.")
 */
export function generateWeekLabels(fcdDate: string, numWeeks: number, locale: string): string[] {
  const fcd = new Date(fcdDate);
  const labels: string[] = [];

  for (let i = 1; i <= numWeeks; i++) {
    const weekDate = new Date(fcd);
    weekDate.setDate(weekDate.getDate() + i * 7);
    const day = weekDate.getDate();
    const month = weekDate.toLocaleDateString(locale, { month: 'short' });
    labels.push(`${day}-${month}`);
  }

  return labels;
}

export function getMonthName(dateString: string, locale: string = navigator.language) {
  const date = new Date(dateString);

  if (isNaN(date.getTime())) return '';

  return date.toLocaleString(locale, { month: 'long' });
}

/** US federal holidays */
type UsFederalHoliday = {
  date: string;
  name: string;
};

function lastWeekdayOfMonth(year: number, monthIndex: number, weekday: number): string {
  const date = new Date(Date.UTC(year, monthIndex + 1, 0));
  const lastDay = date.getUTCDay();
  const offset = (lastDay - weekday + 7) % 7;
  date.setUTCDate(date.getUTCDate() - offset);
  return toIsoDate(date);
}

function nthWeekdayOfMonth(year: number, monthIndex: number, weekday: number, nth: number): string {
  const date = new Date(Date.UTC(year, monthIndex, 1));
  const firstDay = date.getUTCDay();
  const offset = (weekday - firstDay + 7) % 7;
  date.setUTCDate(1 + offset + (nth - 1) * 7);
  return toIsoDate(date);
}

function observedFixedHoliday(year: number, monthIndex: number, day: number): string {
  const date = new Date(Date.UTC(year, monthIndex, day));
  const weekday = date.getUTCDay();
  if (weekday === 6) date.setUTCDate(date.getUTCDate() - 1);
  if (weekday === 0) date.setUTCDate(date.getUTCDate() + 1);
  return toIsoDate(date);
}

export function findNearbyUsFederalHoliday(dateStr: string | undefined, toleranceDays = 1): string {
  if (!dateStr) return '';
  const isoDate = dateStr.slice(0, 10);
  const year = Number(isoDate.slice(0, 4));
  if (!year) return '';

  const holidays = [year - 1, year, year + 1].flatMap((y) => getUsFederalHolidayDates(y));
  const nearby = holidays.find((holiday) => dateDiffInDays(isoDate, holiday.date) <= toleranceDays);
  return nearby?.name ?? '';
}

export function getUsFederalHolidayDates(year: number): UsFederalHoliday[] {
  if (!Number.isInteger(year) || year < 1) return [];

  const holidays: UsFederalHoliday[] = [
    { date: observedFixedHoliday(year, 0, 1), name: "New Year's Day" },
    { date: nthWeekdayOfMonth(year, 0, 1, 3), name: 'Martin Luther King Jr. Day' },
    { date: nthWeekdayOfMonth(year, 1, 1, 3), name: "Presidents' Day" },
    { date: lastWeekdayOfMonth(year, 4, 1), name: 'Memorial Day' },
    { date: observedFixedHoliday(year, 6, 4), name: 'Independence Day' },
    { date: nthWeekdayOfMonth(year, 8, 1, 1), name: 'Labor Day' },
    { date: nthWeekdayOfMonth(year, 9, 1, 2), name: 'Columbus Day' },
    { date: observedFixedHoliday(year, 10, 11), name: 'Veterans Day' },
    { date: nthWeekdayOfMonth(year, 10, 4, 4), name: 'Thanksgiving Day' },
    { date: observedFixedHoliday(year, 11, 25), name: 'Christmas Day' },
  ];

  if (year >= 2021) {
    holidays.splice(4, 0, {
      date: observedFixedHoliday(year, 5, 19),
      name: 'Juneteenth',
    });
  }

  return holidays;
}

/** Forecast helpers */
export function forecastPointDateISO(record: ModelOutputRecord): string | null {
  if (!record?.timestamp || typeof record.span !== 'number') return null;

  const date = parseIsoDateUtc(record.timestamp);
  if (!date) return null;
  date.setUTCDate(date.getUTCDate() + Math.max(record.span - 1, 0) * 7);
  return toIsoDate(date);
}
